import React, { createRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Element, scroller } from 'react-scroll'
import AdminDashboardBanner from 'components/AdminDashboardBanner'
import AdminSortButton from 'components/AdminSortButton'
import DrawerComponent from 'components/DrawerComponent'
import { Tier2ClassHeader } from 'components/admin/ClassDrawerHeader'
import K8Class from 'pages/K8Class'
import {
  ORDER_BY,
  TIER2_LESSON_COMPLETION_SORT_KEYS,
} from 'store/reportsManager/constants'
import { getGradeBandLabel } from 'store/reportsManager/helpers'
import { formatCountLabel } from 'utils/reportsHelpers'
import { pluralizeEnglish } from 'utils/stringHelpers'
import {
  BodyWrapper,
  PageWrapper,
  ContentPanelWrapper,
  StyledSortableAccordionList,
  Row,
  Cell,
  Name,
  Group,
  HeaderTitleRow,
  HeaderTitle,
  TierPill,
  SummaryRow,
  Pipe,
  YellowBullet,
} from './component.styles'

const K8Tier2LessonCompletion = ({
  classDetailsReport,
  classInstance,
  currentPage,
  gradeBand,
  handleOpenClassDrawer,
  handlePaginationSortChange,
  orderBy,
  pageSize,
  selectedProgram,
  siteId,
  sortKey,
  sortedAndPaginatedLessonCompletionData,
  totalItems,
  totalPages,
  incompleteGroupsCount,
}) => {
  if (!siteId) return null

  const closeButtonRef = createRef(null)
  const [isOpen, setIsOpen] = useState(!!classInstance)
  const [fixedView, setFixedView] = useState(false)
  const [fixedTop, setFixedTop] = useState(false)

  // Sync drawer state with classInstance from URL
  useEffect(() => {
    setIsOpen(!!classInstance)
  }, [classInstance])

  const handleScroll = e => {
    const offsetTop = e.target.scrollTop
    if (offsetTop > 22) setFixedTop(true)
    else if (fixedTop) setFixedTop(false)
    if (offsetTop > 52) setFixedView(true)
    else if (fixedView) setFixedView(false)
  }

  const handleCloseDrawer = event => {
    if (
      event.type === 'click' ||
      event.key === 'Enter' ||
      event.key === 'Escape'
    ) {
      event.preventDefault()
      handleOpenClassDrawer(null, siteId)
    }
  }

  useEffect(() => {
    const onKeyPress = e => {
      if (e.key === 'Escape' && isOpen) {
        handleCloseDrawer(e)
      }
    }

    if (isOpen) {
      window.addEventListener('keyup', onKeyPress)
    }

    return () => {
      window.removeEventListener('keyup', onKeyPress)
    }
  }, [isOpen])

  const formatDate = dateString => {
    if (!dateString) return '—'
    return moment(dateString).format('MM/DD/YYYY')
  }

  const buildSortButtons = () => {
    const onSortClick = (clickedSortKey /*, currentSortOrder */) => {
      const isSame = sortKey === clickedSortKey
      const nextOrder = isSame
        ? orderBy === ORDER_BY.ASC
          ? ORDER_BY.DESC
          : ORDER_BY.ASC
        : ORDER_BY.ASC

      scroller.scrollTo('scroll-to-header', {
        duration: 200,
        delay: 50,
        smooth: true,
        offset: 60,
      })

      handlePaginationSortChange({
        newSortKey: clickedSortKey,
        newOrderBy: nextOrder,
        newPage: 1,
      })
    }

    return [
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.SMALL_GROUPS,
        title: 'Small Groups',
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.LESSONS_DONE,
        title: 'Lessons Done',
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.FIRST_UNIT,
        title: 'Emotions',
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.SECOND_UNIT,
        title: 'Relationships',
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.THIRD_UNIT,
        title: 'Executive Function',
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.LAST_LOGGED_IN,
        title: 'Last Logged In',
        handleOnClick: onSortClick,
      },
    ]
  }

  const renderHeader = () => {
    const gradeBandLabel = getGradeBandLabel(gradeBand)

    const hasIncomplete = (incompleteGroupsCount || 0) > 0
    const incompleteCopy = hasIncomplete
      ? `${pluralizeEnglish(incompleteGroupsCount, 'group', 'groups')} ${
          incompleteGroupsCount === 1 ? "hasn't" : "haven't"
        } completed a lesson`
      : ''

    return (
      <>
        <HeaderTitleRow>
          <HeaderTitle>{gradeBandLabel}</HeaderTitle>
          <TierPill>TIER 2</TierPill>
        </HeaderTitleRow>
        <SummaryRow>
          <span>{formatCountLabel(totalItems || 0, 'Group')}</span>
          {hasIncomplete && (
            <>
              <Pipe>|</Pipe>
              <YellowBullet />
              <span>{incompleteCopy}</span>
            </>
          )}
        </SummaryRow>
      </>
    )
  }

  return (
    <PageWrapper>
      <BodyWrapper>
        <AdminDashboardBanner selectedProgram={selectedProgram} />

        <ContentPanelWrapper>
          <Element name="scroll-to-header" />
          <StyledSortableAccordionList
            currentItemCount={
              Array.isArray(sortedAndPaginatedLessonCompletionData)
                ? sortedAndPaginatedLessonCompletionData.length
                : 0
            }
            currentPage={currentPage}
            handlePageChange={newPage => {
              scroller.scrollTo('scroll-to-header', {
                duration: 200,
                delay: 50,
                smooth: true,
                offset: 60,
              })
              handlePaginationSortChange({ newPage })
            }}
            itemLabel="Group"
            items={sortedAndPaginatedLessonCompletionData}
            itemsPerPage={pageSize}
            renderLeftBox={renderHeader}
            renderSortButton={props => <AdminSortButton {...props} />}
            showExportButton={false}
            showHairline
            sortButtons={buildSortButtons()}
            sortKey={sortKey}
            sortOrder={orderBy}
            totalItems={totalItems}
            totalPages={totalPages}
          >
            {item => (
              <Row
                key={`${item.classId || ''}-${item.groupId ||
                  ''}-${item.teacherEmail || Math.random()}`}
                onClick={() =>
                  handleOpenClassDrawer(item.classInstance, siteId)
                }
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOpenClassDrawer(item.classInstance, siteId)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <Cell>
                  <Name>
                    {item.teacherFirstName} {item.teacherLastName}
                  </Name>
                  <Group>{item.groupName}</Group>
                </Cell>
                <Cell>{item.lessonsDone ?? 0}</Cell>
                <Cell>{item.units?.[0]?.lessonsDone ?? 0}</Cell>
                <Cell>{item.units?.[1]?.lessonsDone ?? 0}</Cell>
                <Cell>{item.units?.[2]?.lessonsDone ?? 0}</Cell>
                <Cell>{formatDate(item.lastLoggedIn)}</Cell>
              </Row>
            )}
          </StyledSortableAccordionList>
        </ContentPanelWrapper>
      </BodyWrapper>
      <DrawerComponent
        direction="right"
        handleCloseDrawer={handleCloseDrawer}
        handleScroll={handleScroll}
        isOpen={isOpen}
        ref={closeButtonRef}
      >
        <K8Class
          HeaderComponent={Tier2ClassHeader}
          classDetailsReport={classDetailsReport}
          fixedTop={fixedTop}
          fixedView={fixedView}
          headerProps={{
            classTitle: classDetailsReport?.className || '',
            fixedTop,
            fixedView,
            gradeName: gradeBand ? getGradeBandLabel(gradeBand) : '',
            lessonsCompleted: classDetailsReport?.lessonsCompleted || 0,
            teacherFirstName: classDetailsReport?.teacherFirstName || '',
            teacherLastName: classDetailsReport?.teacherLastName || '',
          }}
        />
      </DrawerComponent>
    </PageWrapper>
  )
}

K8Tier2LessonCompletion.propTypes = {
  classDetailsReport: PropTypes.object,
  classInstance: PropTypes.string,
  currentPage: PropTypes.number,
  gradeBand: PropTypes.string,
  handleOpenClassDrawer: PropTypes.func,
  handlePaginationSortChange: PropTypes.func,
  incompleteGroupsCount: PropTypes.number,
  orderBy: PropTypes.string,
  pageSize: PropTypes.number,
  selectedProgram: PropTypes.object,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sortKey: PropTypes.string,
  sortedAndPaginatedLessonCompletionData: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.oneOf([null]),
  ]),
  totalItems: PropTypes.number,
  totalPages: PropTypes.number,
}

export default K8Tier2LessonCompletion
